import React, { useContext, useState } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    IconButton,
    Alert,
    CircularProgress,
} from '@mui/material';
import { Link } from 'react-router-dom';
import confetti from 'canvas-confetti';
import {
    MailOutline as MailOutlineIcon,
    PhoneIphone as PhoneIphoneIcon,
    AccessTime as AccessTimeIcon,
    LocationOn as LocationOnIcon,
    Facebook as FacebookIcon,
    Instagram as InstagramIcon,
    CheckCircleOutline as CheckCircleOutlineIcon,
} from '@mui/icons-material';
import { ThemeContext } from '../contexts/ThemeContext';

// AI Képzési logók
import LightModeMainLogo from '../assets/images/lightlogo.jpeg';
import DarkModeMainLogo from '../assets/images/darklogo.jpeg';

import LightModeLogo from '../assets/images/LT_FK.jpg';
import DarkModeLogo from '../assets/images/LT_FH.jpg';

// Betűországmeséi logó: a public/ mappában lévő logo.jpeg
const betuorszagmeseiLogo = `${process.env.PUBLIC_URL}/logo.jpeg`;

// Éles környezetben üres (az nginx továbbítja az /api kéréseket)
const API = process.env.REACT_APP_API_URL ?? '';

interface ContactSectionProps {
    handleOpen: () => void;
}

interface FormState {
    name: string;
    email: string;
    subject: string;
    message: string;
    website: string; // honeypot – embernek nem látszik
}

type FormErrors = Partial<Record<'name' | 'email' | 'subject' | 'message', string>>;

const EMPTY_FORM: FormState = { name: '', email: '', subject: '', message: '', website: '' };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const fireConfetti = () => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const colors = ['#C92BB5', '#ffc107', '#4caf50', '#2196f3', '#e91e63', '#ffffff'];
    const end = Date.now() + 1500;

    confetti({ particleCount: 120, spread: 90, origin: { y: 0.65 }, colors });

    const frame = () => {
        confetti({ particleCount: 4, angle: 60, spread: 60, origin: { x: 0, y: 0.7 }, colors });
        confetti({ particleCount: 4, angle: 120, spread: 60, origin: { x: 1, y: 0.7 }, colors });
        if (Date.now() < end) requestAnimationFrame(frame);
    };
    frame();
};

const ContactSection: React.FC<ContactSectionProps> = ({ handleOpen }) => {
    const themeContext = useContext(ThemeContext);

    const [form, setForm] = useState<FormState>(EMPTY_FORM);
    const [errors, setErrors] = useState<FormErrors>({});
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [serverError, setServerError] = useState<string | null>(null);

    if (!themeContext) {
        throw new Error('ContactSection must be used within a ThemeProvider');
    }

    const { darkMode } = themeContext;
    const currentLogo = darkMode ? LightModeLogo : DarkModeLogo;
    const currentMainLogo = darkMode ? LightModeMainLogo : DarkModeMainLogo;

    const contactEmail = 'csillagtanoda.info@gmail.com';
    const phoneNumber = '+36 30/44-74-333';
    const openingHours = 'Folyamatban lévő órák megtekintése';
    const address = 'Kisvárda';
    const socialMediaLinks = {
        facebook: 'https://www.facebook.com/csillagtanoda',
        instagram: 'https://www.instagram.com/csillagtanoda',
        betuorszagmesei: 'https://betuorszagmesei.csillagtanoda.hu/',
    };

    const fieldSx = {
        mt: 0.5,
        '& .MuiOutlinedInput-root': {
            '& fieldset': { borderColor: darkMode ? '#444' : '#ddd' },
            '&:hover fieldset': { borderColor: darkMode ? '#fff' : '#000' },
            '&.Mui-focused fieldset': { borderColor: '#C92BB5' },
        },
        '& .MuiInputBase-input': { color: darkMode ? '#fff' : '#000' },
    };
    const labelSx = { color: darkMode ? '#cccccc' : '#555555', textAlign: 'left' as const };

    const handleChange =
        (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            setForm((prev) => ({ ...prev, [field]: e.target.value }));
            if (field !== 'website' && errors[field as keyof FormErrors]) {
                setErrors((prev) => ({ ...prev, [field]: undefined }));
            }
        };

    const validate = (): FormErrors => {
        const e: FormErrors = {};
        if (!form.name.trim()) e.name = 'Kérjük, add meg a neved.';
        else if (form.name.trim().length > 100) e.name = 'A név legfeljebb 100 karakter lehet.';

        if (!form.email.trim()) e.email = 'Kérjük, add meg az e-mail címed.';
        else if (!EMAIL_RE.test(form.email.trim())) e.email = 'Kérjük, érvényes e-mail címet adj meg.';

        if (!form.subject.trim()) e.subject = 'Kérjük, add meg a tárgyat.';
        else if (form.subject.trim().length > 150) e.subject = 'A tárgy legfeljebb 150 karakter lehet.';

        if (form.message.trim().length < 5) e.message = 'Az üzenet legyen legalább 5 karakter.';
        else if (form.message.trim().length > 5000) e.message = 'Az üzenet legfeljebb 5000 karakter lehet.';
        return e;
    };

    const handleSubmit = async (ev: React.FormEvent) => {
        ev.preventDefault();
        if (sending) return;
        setServerError(null);

        const v = validate();
        setErrors(v);
        if (Object.keys(v).length > 0) return;

        setSending(true);
        try {
            const res = await fetch(`${API}/api/contact`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: form.name.trim(),
                    email: form.email.trim(),
                    subject: form.subject.trim(),
                    message: form.message.trim(),
                    website: form.website,
                }),
            });

            if (res.ok) {
                setSent(true);
                setForm(EMPTY_FORM);
                fireConfetti();
            } else {
                let msg = 'Az üzenetet most nem sikerült elküldeni. Kérjük, próbáld meg később.';
                try {
                    const data = await res.json();
                    if (data?.message) msg = data.message;
                } catch {
                    /* nem JSON válasz */
                }
                setServerError(msg);
            }
        } catch {
            setServerError('Nem sikerült kapcsolódni a szerverhez. Ellenőrizd az internetkapcsolatot, és próbáld újra.');
        } finally {
            setSending(false);
        }
    };

    return (
        <Box
            id="contact-section"
            sx={{
                width: { xs: '92vw', md: '80vw' },
                boxSizing: 'border-box',
                bgcolor: darkMode ? '#120A39' : '#ffffff',
                color: darkMode ? '#ffffff' : '#333333',
                border: '2px solid #6D1B77',
                borderRadius: 2,
                boxShadow: 24,
                p: { xs: 2.5, md: 4 },
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: { xs: 4, md: 8 },
                mx: 'auto',
            }}
        >
            {/* Bal oldal - Űrlap */}
            <Box sx={{ flex: 1, pr: { md: 4 } }}>
                <Typography
                    id="contact-section-title"
                    variant="h4"
                    component="h2"
                    gutterBottom
                    sx={{
                        fontWeight: 'bold',
                        borderBottom: '2px solid #C92BB5',
                        display: 'inline-block',
                        pb: 0.5,
                        color: darkMode ? '#ffffff' : '#000000',
                    }}
                >
                    ÍRJ NEKÜNK!
                </Typography>

                {sent ? (
                    <Box
                        role="status"
                        aria-live="polite"
                        sx={{
                            mt: 3,
                            p: 4,
                            textAlign: 'center',
                            borderRadius: 2,
                            border: `1px solid ${darkMode ? '#2e7d32' : '#a5d6a7'}`,
                            bgcolor: darkMode ? 'rgba(46,125,50,0.12)' : '#f1f8e9',
                        }}
                    >
                        <CheckCircleOutlineIcon sx={{ fontSize: 64, color: '#43a047' }} />
                        <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 1, color: darkMode ? '#fff' : '#000' }}>
                            Köszönjük az üzeneted! 🎉
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 1, color: darkMode ? '#cccccc' : '#555555' }}>
                            Az üzenetet sikeresen elküldted. Hamarosan válaszolunk a megadott e-mail címen.
                        </Typography>
                        <Button
                            variant="outlined"
                            onClick={() => setSent(false)}
                            sx={{
                                mt: 3,
                                color: '#C92BB5',
                                borderColor: '#C92BB5',
                                '&:hover': { borderColor: '#6D1B77', bgcolor: 'rgba(183,28,28,0.08)' },
                            }}
                        >
                            Új üzenet küldése
                        </Button>
                    </Box>
                ) : (
                    <Box
                        component="form"
                        noValidate
                        autoComplete="off"
                        onSubmit={handleSubmit}
                        sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 3 }}
                    >
                        {serverError && (
                            <Alert severity="error" onClose={() => setServerError(null)}>
                                {serverError}
                            </Alert>
                        )}

                        {/* Honeypot: a látogató nem látja, a botok kitöltik */}
                        <Box
                            aria-hidden="true"
                            sx={{ position: 'absolute', left: '-10000px', width: 1, height: 1, overflow: 'hidden' }}
                        >
                            <label htmlFor="contact-website">Weboldal</label>
                            <input
                                id="contact-website"
                                type="text"
                                name="website"
                                tabIndex={-1}
                                autoComplete="off"
                                value={form.website}
                                onChange={handleChange('website')}
                            />
                        </Box>

                        <Box>
                            <Typography variant="body1" sx={labelSx}>Név</Typography>
                            <TextField
                                variant="outlined"
                                fullWidth
                                size="small"
                                value={form.name}
                                onChange={handleChange('name')}
                                error={!!errors.name}
                                helperText={errors.name}
                                disabled={sending}
                                inputProps={{ maxLength: 100 }}
                                sx={fieldSx}
                            />
                        </Box>
                        <Box>
                            <Typography variant="body1" sx={labelSx}>E-mail cím</Typography>
                            <TextField
                                variant="outlined"
                                fullWidth
                                size="small"
                                type="email"
                                value={form.email}
                                onChange={handleChange('email')}
                                error={!!errors.email}
                                helperText={errors.email}
                                disabled={sending}
                                inputProps={{ maxLength: 200 }}
                                sx={fieldSx}
                            />
                        </Box>
                        <Box>
                            <Typography variant="body1" sx={labelSx}>Tárgy</Typography>
                            <TextField
                                variant="outlined"
                                fullWidth
                                size="small"
                                value={form.subject}
                                onChange={handleChange('subject')}
                                error={!!errors.subject}
                                helperText={errors.subject}
                                disabled={sending}
                                inputProps={{ maxLength: 150 }}
                                sx={fieldSx}
                            />
                        </Box>
                        <Box>
                            <Typography variant="body1" sx={labelSx}>Üzenet</Typography>
                            <TextField
                                variant="outlined"
                                fullWidth
                                multiline
                                rows={4}
                                value={form.message}
                                onChange={handleChange('message')}
                                error={!!errors.message}
                                helperText={errors.message}
                                disabled={sending}
                                inputProps={{ maxLength: 5000 }}
                                sx={fieldSx}
                            />
                        </Box>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={sending}
                            sx={{
                                width: sending ? '140px' : '100px',
                                mt: 1,
                                bgcolor: '#C92BB5',
                                color: '#ffffff',
                                '&:hover': { bgcolor: '#6D1B77' },
                            }}
                        >
                            {sending ? (
                                <>
                                    <CircularProgress size={18} sx={{ color: '#fff', mr: 1 }} />
                                    Küldés…
                                </>
                            ) : (
                                'Küldés'
                            )}
                        </Button>
                    </Box>
                )}
            </Box>

            {/* Jobb oldal - Elérhetőségek */}
            <Box sx={{ flex: 1, pt: { xs: 4, md: 0 }, borderLeft: { md: `1px solid ${darkMode ? '#444' : '#ddd'}` }, pl: { md: 4 } }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <IconButton sx={{ bgcolor: '#C92BB5', color: '#fff', '&:hover': { bgcolor: '#6D1B77' } }}>
                            <MailOutlineIcon />
                        </IconButton>
                        <Typography variant="body1" sx={{ color: darkMode ? '#cccccc' : '#555555', textAlign: 'left' }}>
                            {contactEmail}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <IconButton sx={{ bgcolor: '#C92BB5', color: '#fff', '&:hover': { bgcolor: '#6D1B77' } }}>
                            <PhoneIphoneIcon />
                        </IconButton>
                        <Typography variant="body1" sx={{ color: darkMode ? '#cccccc' : '#555555', textAlign: 'left' }}>
                            {phoneNumber} Viber/Whatsapp
                        </Typography>
                    </Box>
                    <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer' }}
                        onClick={handleOpen}
                    >
                        <IconButton sx={{ bgcolor: '#C92BB5', color: '#fff', '&:hover': { bgcolor: '#6D1B77' } }}>
                            <AccessTimeIcon />
                        </IconButton>
                        <Typography variant="body1" sx={{ color: darkMode ? '#cccccc' : '#555555', textAlign: 'left' }}>
                            {openingHours}
                        </Typography>
                        <Box
                            component="img"
                            src={currentMainLogo}
                            alt="Képzések"
                            sx={{ width: 35, height: 35, ml: 1, borderRadius: '50%' }}
                        />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        <IconButton sx={{ bgcolor: '#C92BB5', color: '#fff', '&:hover': { bgcolor: '#6D1B77' } }}>
                            <LocationOnIcon />
                        </IconButton>
                        <Typography variant="body1" sx={{ color: darkMode ? '#cccccc' : '#555555', textAlign: 'left' }}>
                            {address}
                        </Typography>
                    </Box>
                </Box>

                <Typography
                    variant="h6"
                    component="h3"
                    sx={{
                        mt: 5,
                        mb: 2,
                        fontWeight: 'bold',
                        color: darkMode ? '#ffffff' : '#000000',
                        textAlign: 'left',
                    }}
                >
                    KÖVESS MINKET!
                </Typography>

                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Link to={socialMediaLinks.facebook} target="_blank" rel="noopener">
                        <IconButton sx={{ color: darkMode ? '#ffffff' : '#333333', '&:hover': { color: '#C92BB5' } }}>
                            <FacebookIcon />
                        </IconButton>
                    </Link>
                    <Link to={socialMediaLinks.instagram} target="_blank" rel="noopener">
                        <IconButton sx={{ color: darkMode ? '#ffffff' : '#333333', '&:hover': { color: '#C92BB5' } }}>
                            <InstagramIcon />
                        </IconButton>
                    </Link>
                    <a
                        href={socialMediaLinks.betuorszagmesei}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Betűországmeséi"
                    >
                        <IconButton sx={{ '&:hover': { opacity: 0.85 } }}>
                            <Box
                                component="img"
                                src={betuorszagmeseiLogo}
                                alt="Betűországmeséi"
                                sx={{ width: 32, height: 32, objectFit: 'contain', borderRadius: '50%' }}
                            />
                        </IconButton>
                    </a>
                </Box>
                <Box
                    sx={{
                        mt: 3,
                        display: 'flex',
                        justifyContent: 'center',
                        p: 2,
                    }}
                >
                    <Button
                        component={Link}
                        to="/letolthetotartalmak"
                        sx={{
                            mt: 3,
                            p: 0,
                            '&:hover': { bgcolor: 'transparent' },
                        }}
                    >
                        <Box
                            component="img"
                            src={currentLogo}
                            alt="Letölthető tartalmak"
                            sx={{
                                maxWidth: '200px',
                                height: 'auto',
                                borderRadius: 2,
                            }}
                        />
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

export default ContactSection;